import { DateTime } from 'luxon'

import type { resources } from '../i18n/i18n'

export type MonthNumber =
  keyof (typeof resources)['fi']['common']['dateTime']['months']['in']

type MonthInTranslationKey = `common:dateTime.months.in.${MonthNumber}`

// Common date formats
export const DATE_FORMATS = {
  LOCALE_DATE: 'dd.MM.yyyy',
  LOCALE_DATE_SHORT: 'dd.MM',
  TIME: 'HH:mm',
  ISO_DATE: 'yyyy-MM-dd',
} as const

// Returns today's date as YYYY-MM-DD (safe for lexicographic comparison).
export const todayISO = (): string =>
  DateTime.now().toISODate() ?? new Date().toISOString().slice(0, 10)

// Format ISO date string to Finnish locale format (dd.MM.yyyy)
export const formatDate = (isoDateString: string): string => {
  const dateTime = DateTime.fromISO(isoDateString)
  return dateTime.isValid
    ? dateTime.toFormat(DATE_FORMATS.LOCALE_DATE)
    : isoDateString
}

export const formatDatePeriod = (
  startDate: string | null,
  endDate: string | null
): string | null =>
  startDate || endDate
    ? `${startDate ? formatDate(startDate) : ''} - ${endDate ? formatDate(endDate) : ''}`
    : null

// Format ISO date string to short format (dd.MM) if same year, otherwise full format
export const formatDateSmart = (isoDateString: string): string => {
  const dateTime = DateTime.fromISO(isoDateString)
  if (!dateTime.isValid) return isoDateString

  const isCurrentYear = dateTime.year === DateTime.now().year
  return dateTime.toFormat(
    isCurrentYear ? DATE_FORMATS.LOCALE_DATE_SHORT : DATE_FORMATS.LOCALE_DATE
  )
}

// Format ISO datetime string to time format (HH:mm)
export const formatTime = (isoDateTimeString: string): string => {
  const dateTime = DateTime.fromISO(isoDateTimeString)
  return dateTime.isValid
    ? dateTime.toFormat(DATE_FORMATS.TIME)
    : isoDateTimeString
}

// Format time range from two ISO datetime strings
export const formatTimeRange = (
  startIsoDateTime: string,
  endIsoDateTime: string
): string => {
  const startTime = formatTime(startIsoDateTime)
  const endTime = formatTime(endIsoDateTime)
  return `${startTime} - ${endTime}`
}

// Format full reservation datetime: date + time range
// Example: "15.03.2024 14:00 - 16:00"
export const formatReservationDateTime = (
  startIsoDateTime: string,
  endIsoDateTime: string
): string => {
  const date = formatDate(startIsoDateTime)
  const timeRange = formatTimeRange(startIsoDateTime, endIsoDateTime)
  return `${date} ${timeRange}`
}

// Check if date string is valid ISO format
export const isValidISODate = (dateString: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return false
  const date = DateTime.fromISO(dateString)
  return date.isValid
}

// Check if datetime string is valid ISO format
export const isValidISODateTime = (dateTimeString: string): boolean => {
  const dateTime = DateTime.fromISO(dateTimeString)
  return dateTime.isValid
}

// Convert locale date string (dd.MM.yyyy) to ISO format
export const localeToISODate = (localeDateString: string): string => {
  const dateTime = DateTime.fromFormat(
    localeDateString,
    DATE_FORMATS.LOCALE_DATE
  )
  return dateTime.isValid ? dateTime.toISODate() : localeDateString
}

// Safely convert locale date string to ISO format, returns null if invalid
export const safeLocaleToISODate = (
  localeDateString: string | null
): string | null => {
  if (!localeDateString) return null
  const dateTime = DateTime.fromFormat(
    localeDateString,
    DATE_FORMATS.LOCALE_DATE
  )
  return dateTime.isValid ? dateTime.toISODate() : null
}

// Get translation key for weekday from ISO datetime string
// Maps weekday number (1-7) to translation keys like "weekdays.monday".
// Note: the 'reservations' namespace needs to be loaded since the translations are actually there (eg. use useTranslation('reservations') ).
export const getWeekdayTranslationKey = (isoDateTimeString: string) => {
  const dateTime = DateTime.fromISO(isoDateTimeString)
  if (!dateTime.isValid) throw Error(`Invalid date: ${isoDateTimeString}`)

  const weekdayMap = {
    1: 'weekdays.monday',
    2: 'weekdays.tuesday',
    3: 'weekdays.wednesday',
    4: 'weekdays.thursday',
    5: 'weekdays.friday',
    6: 'weekdays.saturday',
    7: 'weekdays.sunday',
  } as const

  return weekdayMap[dateTime.weekday as keyof typeof weekdayMap]
}

export const getMonthInTranslationKey = (
  monthNumber: MonthNumber
): MonthInTranslationKey => `common:dateTime.months.in.${monthNumber}`
